type TagJson = { id: string; label: string; category: string; name: string };
type CheckJson = { existing: TagJson | null; similar: TagJson[]; problem: string | null };

const DELAY_MS = 150;

/**
 * The tag field on the edit and new-page forms. Existing tags are suggested as you type ("npc", "type: n", or an old
 * name like "pc"). A new tag needs "Category: Value" and a deliberate step that first shows look-alikes, so the
 * vocabulary doesn't fill up with duplicates. Chosen tags are hidden TagIds inputs, saved with the page.
 */
export class P9Tags extends HTMLElement {
  #input!: HTMLInputElement;
  #chips!: HTMLUListElement;
  #menu!: HTMLElement;
  #items: Array<{ label: string; run: () => void }> = [];
  #active = -1;
  #timer: number | undefined;
  #sequence = 0;

  connectedCallback(): void {
    const input = this.querySelector<HTMLInputElement>("[data-tag-input]");
    const chips = this.querySelector<HTMLUListElement>("[data-chips]");
    const menu = this.querySelector<HTMLElement>("[data-menu]");
    if (!input || !chips || !menu) return;
    [this.#input, this.#chips, this.#menu] = [input, chips, menu];
    menu.setAttribute("role", "listbox");
    input.setAttribute("role", "combobox");
    input.setAttribute("aria-expanded", "false");

    chips.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest("[data-remove]");
      if (button) button.closest("li")?.remove();
    });
    input.addEventListener("input", () => {
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => void this.#suggest(), DELAY_MS);
    });
    input.addEventListener("focus", () => void this.#suggest());
    input.addEventListener("keydown", (event) => this.#key(event));
    document.addEventListener("mousedown", (event) => {
      if (!this.contains(event.target as Node)) this.#close();
    });
  }

  #key(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      // Never submit the page form from here.
      event.preventDefault();
      const item = this.#items[this.#active >= 0 ? this.#active : 0];
      item?.run();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (this.#items.length === 0) return;
      event.preventDefault();
      this.#active = (this.#active + (event.key === "ArrowDown" ? 1 : -1) + this.#items.length) % this.#items.length;
      this.#highlight();
    } else if (event.key === "Escape") {
      this.#close();
    }
  }

  #chosen(): Set<string> {
    return new Set([...this.#chips.querySelectorAll<HTMLInputElement>('input[name="TagIds"]')].map((i) => i.value));
  }

  #add(tag: TagJson): void {
    this.#input.value = "";
    this.#close();
    if (this.#chosen().has(tag.id)) return;
    const li = document.createElement("li");
    li.className = "tag-chip";
    const text = document.createElement("span");
    text.textContent = tag.label;
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "link";
    remove.dataset.remove = "";
    remove.setAttribute("aria-label", `Remove ${tag.label}`);
    remove.textContent = "×";
    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = "TagIds";
    hidden.value = tag.id;
    li.append(text, remove, hidden);
    this.#chips.append(li);
    hidden.dispatchEvent(new Event("change", { bubbles: true }));
    this.#input.focus();
  }

  async #suggest(): Promise<void> {
    const sequence = ++this.#sequence;
    const text = this.#input.value.trim();
    let tags: TagJson[] = [];
    try {
      const response = await fetch(`${this.dataset.suggestUrl}&q=${encodeURIComponent(text)}`, { headers: { Accept: "application/json" } });
      if (response.ok) tags = (await response.json()) as TagJson[];
    } catch {
      /* offline */
    }
    if (sequence !== this.#sequence) return;
    const chosen = this.#chosen();
    const items: Array<{ label: string; run: () => void }> = tags.filter((t) => !chosen.has(t.id)).map((t) => ({ label: t.label, run: () => this.#add(t) }));

    const [category, value] = splitTag(text);
    const exact = tags.some((t) => same(t.name, value) && (category === null || same(t.category, category)));
    if (category && value && !exact) items.push({ label: `Create new tag “${category}: ${value}”…`, run: () => void this.#check(category, value) });
    this.#show(items, text && !category && !exact ? "To make a new tag, write it as Category: Value, e.g. Type: Player character." : null);
  }

  // The deliberate step: an existing tag with that name is just added; otherwise look-alikes are offered first.
  async #check(category: string, value: string): Promise<void> {
    let check: CheckJson;
    try {
      const query = new URLSearchParams({ category, value });
      const response = await fetch(`${this.dataset.checkUrl}&${query}`, { headers: { Accept: "application/json" } });
      check = (await response.json()) as CheckJson;
    } catch {
      this.#show([], "Couldn't check the tag. Try again.");
      return;
    }
    if (check.problem) return this.#show([], check.problem);
    if (check.existing) return this.#add(check.existing);
    const items = check.similar.map((t) => ({ label: `Use ${t.label}`, run: () => this.#add(t) }));
    items.push({ label: `Create “${category}: ${value}” as a new tag`, run: () => void this.#create(category, value) });
    this.#show(items, check.similar.length > 0 ? "Is it one of these? Using an existing tag keeps the archive tidy." : "No similar tags. Create it?");
  }

  async #create(category: string, value: string): Promise<void> {
    const body = new FormData();
    body.append("category", category);
    body.append("value", value);
    const token = this.closest("form")?.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value ?? "";
    try {
      const response = await fetch(this.dataset.createUrl!, { method: "POST", headers: { RequestVerificationToken: token }, body });
      const result = (await response.json()) as TagJson & { error?: string };
      if (!response.ok) return this.#show([], result.error ?? "Couldn't create the tag.");
      this.#add(result);
    } catch {
      this.#show([], "Couldn't create the tag. Try again.");
    }
  }

  #show(items: Array<{ label: string; run: () => void }>, hint: string | null): void {
    this.#items = items;
    this.#active = -1;
    this.#menu.replaceChildren();
    if (hint) {
      const p = document.createElement("p");
      p.className = "muted small";
      p.textContent = hint;
      this.#menu.append(p);
    }
    items.forEach((item, i) => {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("role", "option");
      button.textContent = item.label;
      button.addEventListener("mousedown", (event) => event.preventDefault());
      button.addEventListener("click", () => {
        this.#active = i;
        item.run();
      });
      this.#menu.append(button);
    });
    this.#menu.hidden = items.length === 0 && !hint;
    this.#input.setAttribute("aria-expanded", String(!this.#menu.hidden));
  }

  #highlight(): void {
    this.#menu.querySelectorAll("button").forEach((b, i) => b.setAttribute("aria-selected", String(i === this.#active)));
  }

  #close(): void {
    this.#menu.hidden = true;
    this.#items = [];
    this.#input.setAttribute("aria-expanded", "false");
  }
}

/** "Type: NPC" → ["Type", "NPC"]; "NPC" → [null, "NPC"]. */
export function splitTag(text: string): [string | null, string] {
  const at = text.indexOf(":");
  return at < 0 ? [null, text.trim()] : [text.slice(0, at).trim(), text.slice(at + 1).trim()];
}

function same(a: string, b: string): boolean {
  const norm = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
  return norm(a) === norm(b);
}

customElements.define("p9-tags", P9Tags);
